"use strict";

(() => {
  window.zag = window.zag || {};

  window.zag.components.zagHeader = class extends (
    window.zag.components.ZagComponent
  ) {
    constructor() {
      super();

      this.DESKTOP_BREAKPOINT = 768;
      this.HIDDEN_SCROLL_THRESHOLD = 500;

      this.lastScrollTop = 0;
    }

    connectedCallback() {
      this.onScroll();
      this.events.add(
        window,
        "scroll",
        window.zag.utils.throttle(this.onScroll),
        { passive: true }
      );

      this.observers.resize(this, this.onResize);

      this.mobileMegaMenuLinkContainers.forEach((linkContainer) => {
        this.events.add(
          linkContainer,
          "click",
          this.onMobileMegaMenuLinkContainerClick
        );
      });

      this.events.add(
        this.mobileMegaMenuCloseButton,
        "click",
        this.onMobileMegaMenuCloseButtonClick
      );

      this.desktopMegaMenuCloseButtons.forEach((closeButton) => {
        this.events.add(
          closeButton,
          "click",
          this.onDesktopMegaMenuCloseButtonClick
        );
      });

      this.desktopMegaMenuLinkContainers.forEach((linkContainer) => {
        this.events.add(
          linkContainer,
          "mouseenter",
          this.onDesktopLinkMouseEnter
        );
        this.events.add(
          linkContainer,
          "mouseleave",
          this.onDesktopLinkMouseLeave
        );
      });

      this.utilityMegaMenuLinkContainers.forEach((linkContainer) => {
        this.events.add(linkContainer, "click", this.onUtilityContainerClick);
      });

      this.events.add(this.overlay, "click", this.onOverlayClick);

      if (!window.zag.utils.documentModifiers.has("template:search")) {
        this.events.add(
          this.quickSearchCloseButton,
          "click",
          this.onUtilityCloseButtonClick
        );
      }
    }

    /**
     * Determines if the user is scrolling down based on the last scroll position.
     * @returns {boolean} True if scrolling down, false otherwise.
     */
    get isScrollingDown() {
      const scrollingDown = window.scrollY > this.lastScrollPosition;
      this.lastScrollPosition = window.scrollY;
      return scrollingDown;
    }

    /**
     * Returns whether the header is hidden.
     * @returns {boolean} True if the header is hidden, false otherwise.
     */
    get isHidden() {
      return this.modifiers.has("hidden");
    }

    /**
     * Sets whether the header is hidden.
     * @param {boolean} value True if the header should be hidden, false otherwise.
     */
    set isHidden(value) {
      this.modifiers.set("hidden", value);
    }

    /**
     * Returns the containers for mobile mega menu links.
     * @returns {HTMLElement[]} An array of containers for mobile mega menu links.
     */
    get mobileMegaMenuLinkContainers() {
      return this.cache.get("dom", "mobileMegaMenuLinkContainers", () => {
        return Array.from(
          this.querySelectorAll(".mobile-link-container[aria-expanded]")
        );
      });
    }

    get mobileMegaMenuCloseButton() {
      return this.cache.get("dom", "mobileMegaMenuCloseButton", () => {
        return this.querySelector(".close-mega-menu-button");
      });
    }

    get desktopMegaMenuCloseButtons() {
      return this.cache.get("dom", "desktopMegaMenuCloseButtons", () => {
        return Array.from(
          this.querySelectorAll(
            ".desktop-link-container .close-desktop-mega-menu-button"
          )
        );
      });
    }

    /**
     * Returns the containers for desktop mega menu links.
     * @returns {HTMLElement[]} An array of containers for desktop mega menu links.
     */
    get desktopMegaMenuLinkContainers() {
      return this.cache.get("dom", "desktopMegaMenuLinkContainers", () => {
        return Array.from(
          this.querySelectorAll(".desktop-link-container[aria-expanded]")
        );
      });
    }

    /**
     * Returns the containers for utility mega menu links.
     * @returns {HTMLElement[]} An array of containers for utility mega menu links.
     */
    get utilityMegaMenuLinkContainers() {
      return this.cache.get("dom", "utilityMegaMenuLinkContainers", () => {
        return Array.from(
          this.querySelectorAll(".utility-link-container[aria-expanded]")
        );
      });
    }

    get overlay() {
      return this.cache.get("dom", "overlay", () => {
        return this.section.querySelector("zag-overlay");
      });
    }

    get quickSearchCloseButton() {
      return this.cache.get("dom", "quickSearchCloseButton", () => {
        return this.querySelector("zag-search .close-button");
      });
    }

    onScroll = () => {
      if (
        window.scrollY > this.HIDDEN_SCROLL_THRESHOLD &&
        !this.modifiers.has("mega-menu-open")
      ) {
        this.isHidden = this.isScrollingDown;
      } else {
        this.isHidden = false;
      }

      if (window.scrollY > 0) {
        this.modifiers.add("sticky");
      } else {
        this.modifiers.remove("sticky");
      }
    };

    onResize = () => {
      this.cache.clear(["layout", "style"]);

      this.onScroll();
    };

    onUtilityContainerClick = (event) => {
      if (!event.target.closest(".utility-link")) return;

      this.mobileMegaMenuCloseButton.click();

      const menuOpened = this.utilityMegaMenuLinkContainers.some(
        (linkContainer) =>
          linkContainer.getAttribute("aria-expanded") === "true"
      );
      const targetMenuOpened =
        event.currentTarget.getAttribute("aria-expanded") === "true";

      if (targetMenuOpened) {
        this.modifiers.remove("mega-menu-open");
        this.modifiers.remove("utility-mega-menu-open");
        this.modifiers.remove("mobile-menu-open");
        this.modifiers.remove("overlay");
        event.currentTarget.setAttribute("aria-expanded", "false");
      } else if (menuOpened) {
        this.modifiers.add("mega-menu-open");
        this.modifiers.add("utility-mega-menu-open");
        this.modifiers.add("overlay");
        event.currentTarget.getAttribute("data-utility") === "menu"
          ? this.modifiers.add("mobile-menu-open")
          : this.modifiers.remove("mobile-menu-open");

        this.utilityMegaMenuLinkContainers.forEach((linkContainer) => {
          linkContainer === event.currentTarget
            ? linkContainer.setAttribute("aria-expanded", "true")
            : linkContainer.setAttribute("aria-expanded", "false");
        });
      } else {
        this.modifiers.add("mega-menu-open");
        this.modifiers.add("utility-mega-menu-open");
        this.modifiers.add("overlay");
        if (event.currentTarget.getAttribute("data-utility") === "menu") {
          this.modifiers.add("mobile-menu-open");
        }

        event.currentTarget.setAttribute("aria-expanded", "true");
      }

      if (window.screen.width < this.DESKTOP_BREAKPOINT) {
        window.zag.utils.documentScroll.isLocked
          ? (window.zag.utils.documentScroll.isLocked = false)
          : (window.zag.utils.documentScroll.isLocked = true);
      }
    };

    onMobileMegaMenuLinkContainerClick = (event) => {
      if (event.target.classList.contains("mobile-menu-link")) {
        event.preventDefault();
      }

      this.mobileMegaMenuCloseButton.click();

      event.currentTarget.setAttribute("aria-expanded", "true");
    };

    onMobileMegaMenuCloseButtonClick = () => {
      this.mobileMegaMenuLinkContainers.forEach((linkContainer) => {
        linkContainer.setAttribute("aria-expanded", "false");
      });
    };

    onDesktopMegaMenuCloseButtonClick = (event) => {
      this.modifiers.remove("mega-menu-open");
      this.modifiers.remove("desktop-mega-menu-open");
      this.modifiers.remove("overlay");

      event.currentTarget
        .closest(".desktop-link-container")
        .setAttribute("aria-expanded", "false");
    };

    onDesktopLinkMouseEnter = (event) => {
      this.modifiers.remove("utility-mega-menu-open");
      this.modifiers.remove("mobile-menu-open");
      this.modifiers.remove("overlay");
      this.utilityMegaMenuLinkContainers.forEach((linkContainer) => {
        linkContainer.setAttribute("aria-expanded", "false");
      });

      this.modifiers.add("mega-menu-open");
      this.modifiers.add("desktop-mega-menu-open");
      this.modifiers.add("overlay");

      event.currentTarget.setAttribute("aria-expanded", "true");
    };

    onDesktopLinkMouseLeave = (event) => {
      this.modifiers.remove("mega-menu-open");
      this.modifiers.remove("desktop-mega-menu-open");
      this.modifiers.remove("overlay");
      event.currentTarget.setAttribute("aria-expanded", "false");
    };

    onOverlayClick = () => {
      const targetElement = this.querySelector(
        ".utility-link-container[aria-expanded='true'] .utility-link"
      );
      if (!targetElement) return;

      targetElement.click();
    };

    onUtilityCloseButtonClick = (event) => {
      event.currentTarget
        .closest(".utility-link-container")
        .querySelector(".utility-link")
        .click();
    };
  };
  if (!customElements.get("zag-header")) {
    customElements.define("zag-header", window.zag.components.zagHeader);
  }
})();
